import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const mainLinks = [
  { label: '首頁', href: '/' },
  { label: '關於鳴箏', href: '/about' },
  { label: '教學方式', href: '/teaching' },
  { label: '課程路徑', href: '/courses' },
  { label: '古箏買賣', href: '/instrument' },
  { label: '鳴箏筆記', href: '/notes' },
  { label: '聯絡諮詢', href: '/contact' },
];

const isExactActive = (href: string, pathname: string) =>
  href === '/' ? pathname === '/' : pathname === href || pathname.startsWith(href + '/');

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-[#faf8f5] backdrop-blur-xl border-b border-[#ddd0bd]'
          : 'bg-[#faf8f5]/85 backdrop-blur-md border-b border-[#e0d5c5]/40'
      }`}
    >
      <div className="w-full px-6 md:px-12 lg:px-20">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo — enlarged to ~80px for clearer presence */}
          <Link to="/" className="flex items-center gap-3 cursor-pointer flex-shrink-0">
            <img
              src="https://static.readdy.ai/image/c58d2dc5fdc4f4caa90264537c733184/e33f307f87b4fb2fac52df6e599e8335.png"
              alt="鳴箏 Logo"
              className="h-16 md:h-[5rem] w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-10">
            {mainLinks.map((link) => {
              const active = isExactActive(link.href, location.pathname);
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-[1.0625rem] font-semibold tracking-wide transition-all duration-300 whitespace-nowrap cursor-pointer rounded-full ${
                    active
                      ? 'text-[#050200] bg-[#e8ddd0]/90 px-4 py-2 -mx-4'
                      : 'text-[#050200] hover:text-[#050200] hover:bg-[#e8ddd0]/55 px-4 py-2 -mx-4'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* CTA — filled, clearly distinct from nav text */}
          <div className="hidden md:flex items-center flex-shrink-0">
            <a
              href="https://line.me"
              target="_blank"
              rel="noopener nofollow"
              className="px-6 py-3 text-sm font-extrabold rounded-full bg-[#4a2e18] text-white hover:bg-[#2c1a0c] transition-all duration-300 whitespace-nowrap cursor-pointer tracking-wider"
            >
              加入 LINE@ 諮詢
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden w-12 h-12 flex items-center justify-center cursor-pointer flex-shrink-0"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="開啟選單"
          >
            <i className={`text-2xl text-[#050200] ${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-400 bg-[#faf8f5]/98 backdrop-blur-xl ${
          menuOpen ? 'max-h-[700px] border-b border-[#e0d5c5]' : 'max-h-0'
        }`}
      >
        <nav className="px-6 py-6 flex flex-col gap-1">
          {mainLinks.map((link) => {
            const active = isExactActive(link.href, location.pathname);
            return (
              <Link
                key={link.href}
                to={link.href}
                className={`text-[1.125rem] font-semibold transition-all cursor-pointer whitespace-nowrap px-4 py-4 rounded-xl ${
                  active
                    ? 'text-[#050200] bg-[#e8ddd0]'
                    : 'text-[#050200] hover:text-[#050200] hover:bg-[#e8ddd0]/60'
                }`}
              >
                {link.label}
              </Link>
            );
          })}
          <a
            href="https://line.me"
            target="_blank"
            rel="noopener nofollow"
            className="mt-4 px-6 py-3.5 text-sm font-extrabold rounded-full bg-[#4a2e18] text-white text-center cursor-pointer whitespace-nowrap hover:bg-[#2c1a0c] transition-all duration-300 tracking-wider"
          >
            加入 LINE@ 諮詢
          </a>
        </nav>
      </div>
    </header>
  );
}